import { AskRequest } from '@hone-automation/common';
import { Transaction } from './TransactionsTable';
import { RequestStatus } from 'presentation/components/Queue/RequestItem/type';
import { normalizeStatus } from 'presentation/components/Queue/utils/askRequestUtils';

export const getFlaggingTooltipMessage = (
  allowFlagging: boolean,
  isConsolidated: boolean,
  isBeginningBalance: boolean = false
): string => {
  if (isBeginningBalance) {
    return 'Beginning Balance transactions cannot be flagged';
  }
  if (!allowFlagging && isConsolidated) {
    return 'Flagging is disabled on consolidated mode';
  } else if (!allowFlagging) {
    return 'Flagging is not allowed for this report';
  }
  return '';
};

/** Check if an AskRequest is internal (all comments have Internal visibility) */
export const isInternalRequest = (request: AskRequest): boolean => {
  if (!request.comments || request.comments.length === 0) return false;
  return request.comments.every(comment => (comment.visibility ?? '').toLowerCase() === 'internal');
};

/** Convert a status string to camelCase for CSS class usage */
export const toCamelCase = (status: string): string =>
  status
    .toLowerCase()
    .split(/\s+|_+/)
    .map((word, idx) => (idx === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
    .join('');

/** Match a transaction against flaggedTransactions by eventId/externalId AND amount */
export const findTransactionLevelMatch = (
  transaction: Transaction,
  flaggedTransactions: AskRequest[] | undefined
): AskRequest | undefined => {
  return flaggedTransactions?.find(flagged => {
    if (transaction.eventId && flagged.transaction?.externalId) {
      if (transaction.eventId !== flagged.transaction.externalId) return false;
      const flaggedAmount = Number(flagged.flaggedAmount) || Number(flagged.transaction?.amount);
      if (flaggedAmount && Number(transaction.amount) !== flaggedAmount) return false;
      return true;
    }
    return false;
  });
};

/** Match a transaction against cellTrxFlagged with fallback to date+amount+type+memo */
export const findCellTrxMatch = (
  transaction: Transaction,
  cellTrxFlagged: AskRequest[] | undefined
): AskRequest | undefined => {
  if (!cellTrxFlagged || cellTrxFlagged.length === 0) return undefined;

  return cellTrxFlagged.find(flagged => {
    // Match by eventId/externalId AND amount (eventId may be a batch/JE ID)
    if (transaction.eventId && flagged.transaction?.externalId) {
      if (transaction.eventId !== flagged.transaction.externalId) return false;
      const flaggedAmount = Number(flagged.flaggedAmount) || Number(flagged.transaction?.amount);
      if (flaggedAmount && Number(transaction.amount) !== flaggedAmount) return false;
      return true;
    }

    // Match by transaction details if externalId not available
    if (flagged.transaction) {
      const trx = flagged.transaction;
      // Match by amount
      const amountMatches =
        Number(flagged.flaggedAmount) === Number(transaction.amount) ||
        Number(trx.amount) === Number(transaction.amount);
      if (!amountMatches) return false;

      // Match by date (trx.date may be Date object, convert to string for comparison)
      const txDate = transaction.period || transaction.date;
      const flagDateRaw = trx.date || flagged.period;
      const flagDate = flagDateRaw instanceof Date ? flagDateRaw.toISOString().split('T')[0] : flagDateRaw;
      const dateMatches =
        txDate &&
        flagDate &&
        (txDate === flagDate || txDate.startsWith(flagDate as string) || (flagDate as string).startsWith(txDate));
      if (!dateMatches) return false;

      // Additional matching for disambiguation
      if (trx.externalTransactionType && transaction.type !== trx.externalTransactionType) return false;
      if (trx.memo && transaction.memo !== trx.memo) return false;

      return true;
    }

    return false;
  });
};

/** Orchestrator: determine a transaction's flagged status as a camelCase RequestStatus */
export const getTransactionFlaggedStatus = (
  transaction: Transaction,
  flaggedTransactions: AskRequest[] | undefined,
  cellTrxFlagged: AskRequest[] | undefined
): RequestStatus | undefined => {
  const transactionLevelMatch = findTransactionLevelMatch(transaction, flaggedTransactions);
  if (transactionLevelMatch) {
    const status = transactionLevelMatch.status;
    if (!status) return undefined;
    return toCamelCase(status) as RequestStatus;
  }

  const cellMatch = findCellTrxMatch(transaction, cellTrxFlagged);
  if (cellMatch) {
    return toCamelCase(cellMatch.status || 'Open') as RequestStatus;
  }

  return undefined;
};

/** Check if a transaction's matched flag is internal */
export const isTransactionFlagInternal = (
  transaction: Transaction,
  flaggedTransactions: AskRequest[] | undefined,
  cellTrxFlagged: AskRequest[] | undefined
): boolean => {
  const match =
    findTransactionLevelMatch(transaction, flaggedTransactions) || findCellTrxMatch(transaction, cellTrxFlagged);
  return match ? isInternalRequest(match) : false;
};

/** Find the flagged cell matching the selected transaction, with AP Aging special case */
export const findFlaggedCell = (
  selectedTransaction: { title?: string; date?: string } | null | undefined,
  flaggedCells: AskRequest[] | undefined,
  reportType: string | undefined
): (AskRequest & { status: string | undefined }) | undefined => {
  if (!selectedTransaction) return undefined;

  let foundCell: AskRequest | undefined;
  if (reportType === 'AP Aging') {
    foundCell = flaggedCells?.find(flagged => {
      return flagged.identifier === selectedTransaction.title;
    });
  } else {
    foundCell = flaggedCells?.find(flagged => {
      return flagged.identifier === selectedTransaction.title && flagged.period === selectedTransaction.date;
    });
  }

  if (!foundCell) return undefined;
  const normalizedStatus = normalizeStatus(foundCell?.status);
  return {
    ...foundCell,
    status: normalizedStatus,
  } as AskRequest & { status: string | undefined };
};

/** Case-insensitive search filtering across name, memo, type, and reference */
export const filterTransactions = (transactions: Transaction[], search: string): Transaction[] => {
  if (!search) return transactions;
  return transactions.filter(
    transaction =>
      transaction.name.toLowerCase().includes(search.toLowerCase()) ||
      transaction.memo.toLowerCase().includes(search.toLowerCase()) ||
      transaction.type.toLowerCase().includes(search.toLowerCase()) ||
      (transaction.reference && transaction.reference.toLowerCase().includes(search.toLowerCase()))
  );
};

/** Sort transactions by a given key and direction */
export const sortTransactions = (
  transactions: Transaction[],
  sortKey: string,
  sortDir: 'asc' | 'desc'
): Transaction[] => {
  const sorted = [...transactions].sort((a, b) => {
    let aValue: any = a[sortKey as keyof Transaction];
    let bValue: any = b[sortKey as keyof Transaction];

    switch (sortKey) {
      case 'date':
        aValue = new Date(a.date).getTime();
        bValue = new Date(b.date).getTime();
        break;
      case 'amount':
      case 'balance':
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
        break;
      case 'dueDate':
        aValue = new Date(a.dueDate || a.date).getTime();
        bValue = new Date(b.dueDate || b.date).getTime();
        break;
      default:
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
    }

    if (aValue < bValue) return sortDir === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });
  return sorted;
};
